import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { enqueueSnackbar } from "notistack";
import color from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useComoAgents, useDeleteComoAgent } from "../../Query/useComoAgent";
import { useLLMModels } from "../../Query/useLLM";
import { useState } from "react";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { getModelIcon } from "../../Helpers/modelHelpers";
import { useTranslation } from "react-i18next";

const ComoAgentsList = () => {
  const navigate = useNavigate();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const { mutate: deleteAgent } = useDeleteComoAgent();
  const { data: llmData } = useLLMModels();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("comoAgentForm");

  const getModelName = (modelId: number | null) => {
    if (modelId === null) return t("none");
    const model = (llmData?.all_models || []).find(
      (m: { model_id: number; model_name: string }) => m.model_id === modelId,
    );
    return model?.model_name || t("modelWithId", { id: modelId });
  };

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedAgentId) {
      deleteAgent(selectedAgentId);
    }
  };

  const handleCopyId = (id: number) => {
    navigator.clipboard
      .writeText(String(id))
      .then(() => enqueueSnackbar(t("idCopied"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("failedToCopyId"), { variant: "error" }));
  };

  const { data, isLoading, isError } = useComoAgents();

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: color.red700 }}>
        Error: {t("failedToFetchAgents")}
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: color.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <GroupsOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("virtualTeamMembers")}
                </Typography>
                <Typography variant="caption" sx={{ color: color.gray500 }}>
                  {data?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/como_agents/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createNew")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${color.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: color.gray50 }}>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: "8%",
                    }}
                  >
                    {t("id")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: "27%",
                    }}
                  >
                    {t("name")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: "22%",
                    }}
                  >
                    {t("defaultModel")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: "25%",
                    }}
                  >
                    {t("tools")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      width: "20%",
                    }}
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data ?? []).map((agent) => {
                  const modelName = getModelName(agent.default_model);
                  const modelIcon =
                    agent.default_model !== null
                      ? getModelIcon(modelName)
                      : null;
                  return (
                    <TableRow
                      key={agent.id}
                      sx={{
                        "&:hover": { bgcolor: color.gray50 },
                        cursor: "pointer",
                      }}
                      onClick={() => navigate(`/como_agents/edit/${agent.id}`)}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: color.gray500,
                              fontFamily: "monospace",
                            }}
                          >
                            {agent.id}
                          </Typography>
                          <Tooltip title={t("copyId")}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyId(agent.id)}
                              sx={{
                                color: color.gray400,
                                "&:hover": { color: workspace.primary_dark },
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: color.primary_dark }}
                        >
                          {agent.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {modelIcon && (
                            <img
                              src={modelIcon}
                              alt=""
                              style={{
                                width: 16,
                                height: 16,
                                objectFit: "contain",
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{ color: color.gray600 }}
                          >
                            {modelName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(agent.tools || []).length > 0 ? (
                            agent.tools.map((tool) => (
                              <Chip
                                key={tool}
                                label={tool.replace(/_/g, " ")}
                                size="small"
                                sx={{
                                  bgcolor: color.blue50,
                                  color: color.blue700,
                                  fontSize: "0.75rem",
                                  height: 24,
                                }}
                              />
                            ))
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: color.gray400 }}
                            >
                              {t("noTools")}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title={t("edit")}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(`/como_agents/edit/${agent.id}`)
                              }
                              sx={{ color: color.secondary }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("delete")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedAgentId(agent.id);
                                setConfirmationDialogOpen(true);
                              }}
                              sx={{
                                color: color.gray400,
                                "&:hover": { color: color.red },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(data ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: color.gray400 }}>
                        {t("noVirtualTeamMembersYet")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        title={t("deleteAgent")}
        description={t("deleteAgentDescription")}
        content={t("deleteAgentContent")}
        actions={[
          {
            label: t("no"),
            onClick: () => handleConfirmationDialogClose(false),
          },
          {
            label: t("yes"),
            onClick: () => handleConfirmationDialogClose(true),
          },
        ]}
      />
    </Box>
  );
};

export default ComoAgentsList;
